// App-wide offline write outbox.
//
// Any screen (patients, invoices, notes, vitals, tasks, ...) can queue a write
// while the device is offline. Writes are stored in IndexedDB with a monotonic
// sequence number and replayed in order when connectivity returns — on desktop,
// laptop and mobile alike, because everything runs in the browser/Electron
// renderer.
//
// Idempotency: every queued row carries a client-generated `id` (uuid) used as
// the primary key, so replaying an already-applied insert conflicts on the
// primary key and is treated as success.

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { supabase } from "@/integrations/supabase/client";
import { CONFLICT_TABLES, diffConflict, recordConflict } from "@/lib/sync-conflicts";
import { trackSync } from "@/lib/sync-audit";

const DB_NAME = "doctorsx-offline-outbox";
const DB_VERSION = 1;
const STORE = "pending_writes";

export type OutboxOp = "insert" | "update" | "delete" | "upsert";

export interface PendingWrite {
  /** uuid of the outbox entry */
  key: string;
  /** target table in the public schema */
  table: string;
  op: OutboxOp;
  /** row payload for insert/update/upsert */
  payload?: Record<string, unknown>;
  /** primary key value for update/delete */
  rowId?: string;
  /** row snapshot at edit time — used to detect multi-device conflicts */
  baseline?: Record<string, unknown>;
  /** human label used in the sync UI */
  label?: string;

  seq: number;
  queued_at: number;
  attempts: number;
  next_attempt_at: number;
  last_error?: string;
}

interface OutboxSchema extends DBSchema {
  pending_writes: {
    key: string;
    value: PendingWrite;
    indexes: { by_seq: number };
  };
}

let dbPromise: Promise<IDBPDatabase<OutboxSchema>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<OutboxSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: "key" });
          store.createIndex("by_seq", "seq");
        }
      },
    });
  }
  return dbPromise;
}

/* ------------------------------------------------------------------ */
/* Subscriptions (cross-tab via BroadcastChannel)                      */
/* ------------------------------------------------------------------ */

type Listener = (items: PendingWrite[]) => void;
const listeners = new Set<Listener>();
const channel =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel("doctorsx-offline-outbox")
    : null;

async function notify() {
  const items = await listPendingWrites();
  listeners.forEach((l) => l(items));
}

async function emit() {
  await notify();
  channel?.postMessage({ type: "changed" });
}

channel?.addEventListener("message", () => {
  void notify();
});

export function subscribeOfflineOutbox(fn: Listener): () => void {
  listeners.add(fn);
  void listPendingWrites().then(fn);
  return () => {
    listeners.delete(fn);
  };
}

export async function listPendingWrites(): Promise<PendingWrite[]> {
  const db = await getDb();
  const all = await db.getAll(STORE);
  return all.sort((a, b) => a.seq - b.seq);
}

export async function countPendingWrites(): Promise<number> {
  const db = await getDb();
  return db.count(STORE);
}

async function nextSeq(db: IDBPDatabase<OutboxSchema>) {
  const all = await db.getAll(STORE);
  return all.reduce((max, r) => Math.max(max, r.seq), 0) + 1;
}

function uuid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Queue a write for later replay. Returns the outbox key. */
export async function queueWrite(entry: {
  table: string;
  op: OutboxOp;
  payload?: Record<string, unknown>;
  rowId?: string;
  baseline?: Record<string, unknown>;
  label?: string;
}): Promise<string> {
  const db = await getDb();
  const key = uuid();
  await db.put(STORE, {
    key,
    table: entry.table,
    op: entry.op,
    payload: entry.payload,
    rowId: entry.rowId,
    baseline: entry.baseline,
    label: entry.label,

    seq: await nextSeq(db),
    queued_at: Date.now(),
    attempts: 0,
    next_attempt_at: 0,
  });
  trackSync({
    source: "write",
    ref: key,
    status: "queued",
    table: entry.table,
    op: entry.op,
    label: entry.label,
  });
  await emit();
  void requestBackgroundSync();
  return key;
}

export async function removePendingWrite(key: string) {
  const db = await getDb();
  await db.delete(STORE, key);
  await emit();
}

export async function clearPendingWrites() {
  const db = await getDb();
  await db.clear(STORE);
  await emit();
}

/* ------------------------------------------------------------------ */
/* Online-first helper                                                 */
/* ------------------------------------------------------------------ */

function isNetworkError(err: unknown) {
  const msg = String((err as { message?: string })?.message ?? err ?? "").toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("load failed") ||
    msg.includes("timeout") ||
    msg.includes("offline")
  );
}

/** Treat "row already there" as success so replays stay idempotent. */
function isDuplicate(err: { code?: string; message?: string } | null) {
  if (!err) return false;
  return err.code === "23505" || String(err.message ?? "").includes("duplicate key");
}

async function apply(entry: Pick<PendingWrite, "table" | "op" | "payload" | "rowId">) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const from = supabase.from(entry.table as any) as any;
  if (entry.op === "insert") return from.insert(entry.payload);
  if (entry.op === "upsert") return from.upsert(entry.payload);
  if (entry.op === "update") return from.update(entry.payload).eq("id", entry.rowId);
  return from.delete().eq("id", entry.rowId);
}

/**
 * For conflict-sensitive tables (appointments, invoices), verify no other
 * device changed the same fields since this edit was made. Returns true when a
 * conflict was parked for the doctor to resolve — the write must NOT be applied.
 */
async function guardConflict(
  entry: Pick<PendingWrite, "table" | "op" | "payload" | "rowId" | "baseline" | "label"> & { queued_at?: number },
): Promise<boolean> {
  if (entry.op !== "update" || !entry.rowId || !entry.baseline) return false;
  if (!CONFLICT_TABLES.has(entry.table)) return false;
  try {
    const { data, error } = await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from(entry.table as any)
      .select("*")
      .eq("id", entry.rowId)
      .maybeSingle();
    if (error || !data) return false;
    const serverRow = data as unknown as Record<string, unknown>;
    const fields = diffConflict(entry.payload ?? {}, entry.baseline, serverRow);
    if (fields.length === 0) return false;
    await recordConflict({
      table: entry.table,
      rowId: entry.rowId,
      label: entry.label,
      payload: entry.payload ?? {},
      serverRow,
      fields,
      queued_at: entry.queued_at,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Run a write online when possible, otherwise queue it and resolve optimistically.
 * Returns `{ queued: true }` when the write was deferred, `{ conflict: true }`
 * when another device changed the same fields and a decision is needed.
 */
export async function writeWithOutbox(entry: {
  table: string;
  op: OutboxOp;
  payload?: Record<string, unknown>;
  rowId?: string;
  baseline?: Record<string, unknown>;
  label?: string;
}): Promise<{ queued: boolean; conflict?: boolean }> {
  const offline = typeof navigator !== "undefined" && !navigator.onLine;
  if (offline) {
    await queueWrite(entry);
    return { queued: true };
  }
  try {
    if (await guardConflict(entry)) return { queued: false, conflict: true };
    const { error } = await apply(entry);
    if (error && !isDuplicate(error)) {
      if (isNetworkError(error)) {
        await queueWrite(entry);
        return { queued: true };
      }
      throw error;
    }
    return { queued: false };
  } catch (err) {
    if (isNetworkError(err)) {
      await queueWrite(entry);
      return { queued: true };
    }
    throw err;
  }
}


/* ------------------------------------------------------------------ */
/* Replay                                                              */
/* ------------------------------------------------------------------ */

export interface OfflineSyncState {
  syncing: boolean;
  total: number;
  done: number;
  lastSyncedAt: number | null;
  lastError: string | null;
}

let state: OfflineSyncState = {
  syncing: false,
  total: 0,
  done: 0,
  lastSyncedAt: null,
  lastError: null,
};
const stateListeners = new Set<(s: OfflineSyncState) => void>();

export function getOfflineSyncState() {
  return state;
}

export function subscribeOfflineSyncState(fn: (s: OfflineSyncState) => void) {
  stateListeners.add(fn);
  fn(state);
  return () => {
    stateListeners.delete(fn);
  };
}

function setState(patch: Partial<OfflineSyncState>) {
  state = { ...state, ...patch };
  stateListeners.forEach((l) => l(state));
}

const MAX_ATTEMPTS = 8;
function backoffMs(attempts: number) {
  const base = Math.min(60_000, 1000 * 2 ** attempts);
  return base + Math.random() * 500; // jitter
}

let running: Promise<{ synced: number; failed: number }> | null = null;

/** Replay every queued write in sequence order. Safe to call repeatedly. */
export function syncOfflineOutbox() {
  if (running) return running;
  running = (async () => {
    let synced = 0;
    let failed = 0;
    const db = await getDb();
    const items = await listPendingWrites();
    if (items.length === 0) {
      setState({ syncing: false, total: 0, done: 0 });
      return { synced, failed };
    }
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      return { synced, failed: items.length };
    }

    setState({ syncing: true, total: items.length, done: 0, lastError: null });

    for (const item of items) {
      if (item.next_attempt_at > Date.now()) {
        failed += 1;
        continue;
      }
      try {
        if (await guardConflict(item)) {
          // Parked as a conflict — remove from the queue, the doctor decides.
          await db.delete(STORE, item.key);
          trackSync({ source: "write", ref: item.key, status: "conflict", table: item.table, op: item.op, label: item.label });
          synced += 1;
          setState({ done: synced });
          continue;
        }
        const { error } = await apply(item);

        if (error && !isDuplicate(error)) throw error;
        await db.delete(STORE, item.key);
        trackSync({ source: "write", ref: item.key, status: "synced", table: item.table, op: item.op, label: item.label, attempt: item.attempts + 1 });
        synced += 1;
      } catch (err) {
        failed += 1;
        const attempts = item.attempts + 1;
        const message = String((err as { message?: string })?.message ?? err);
        if (attempts >= MAX_ATTEMPTS && !isNetworkError(err)) {
          // Permanently rejected by the backend — drop it but surface the reason.
          await db.delete(STORE, item.key);
          trackSync({ source: "write", ref: item.key, status: "dropped", table: item.table, op: item.op, label: item.label, attempt: attempts, error: message });
        } else {
          trackSync({ source: "write", ref: item.key, status: "retry", table: item.table, op: item.op, label: item.label, attempt: attempts, error: message });
          await db.put(STORE, {
            ...item,
            attempts,
            last_error: message,
            next_attempt_at: Date.now() + backoffMs(attempts),
          });
        }
        setState({ lastError: message });
        if (isNetworkError(err)) break; // network died again — stop early
      }
      setState({ done: synced });
    }

    setState({
      syncing: false,
      lastSyncedAt: synced > 0 ? Date.now() : state.lastSyncedAt,
    });
    await emit();
    return { synced, failed };
  })().finally(() => {
    running = null;
  });
  return running;
}

async function requestBackgroundSync() {
  try {
    if (!("serviceWorker" in navigator)) return;
    const reg = (await navigator.serviceWorker.ready) as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> };
    };
    await reg.sync?.register("outbox-drain");
  } catch {
    /* Background Sync unsupported (iOS) — online listener handles it */
  }
}

let installed = false;

/**
 * Install auto-sync triggers: reconnect, tab focus, periodic retry and
 * service-worker background-sync messages. Idempotent.
 */
export function initOfflineOutbox(onSynced?: (r: { synced: number; failed: number }) => void) {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const run = async () => {
    if (!navigator.onLine) return;
    const r = await syncOfflineOutbox();
    if (r.synced > 0) onSynced?.(r);
  };

  window.addEventListener("online", () => void run());
  window.addEventListener("focus", () => void run());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void run();
  });
  navigator.serviceWorker?.addEventListener?.("message", (event: MessageEvent) => {
    if ((event.data as { type?: string })?.type === "drain-outbox") void run();
  });
  // Retry stragglers (respecting backoff) every 30s.
  setInterval(() => void run(), 30_000);

  void run();
}
